import { useQuery } from '@tanstack/react-query';
import { Link, useParams, useSearch } from '@tanstack/react-router';
import {
  Alert,
  backLinkClasses,
  Badge,
  Button,
  Card,
  DataList,
  DataRow,
  linkTextClasses,
  NotProvided,
  Page,
  PageHeader,
  Table,
  isNotFound,
  NotFoundError,
} from '@evogard/ui';
import {
  canAccess,
  formatDate,
  formatInstant,
  formatPercentage,
  loadFailureMessage,
} from '@evogard/domain';
import { MoneyValue } from '../../components/Values.js';
import { backSearch, useTablePage } from '../../lib/list-search.js';
import { api } from '../../lib/api.js';
import { useSessionContext } from '../../lib/session.js';
import { MirrorStamp, RefreshOutcome, StatusBadge, useErpRefresh } from './common.js';
import { BoletosTable, ChangesCard } from './history.js';
import { MissingRecord } from '../../components/MissingRecord.js';

/**
 * Um veículo, o dono, os produtos e os boletos dele — `erp-38`.
 *
 * ## As três coisas numa página, e por que juntas
 *
 * Quem atende recebe uma placa e precisa responder três perguntas seguidas: que
 * carro é, de quem é, e o que ele deve. Em três telas, a pessoa do outro lado do
 * telefone espera duas navegações — e a segunda costuma ser "qual era mesmo o
 * código do associado?".
 *
 * ## Os boletos são DAQUELE carro
 *
 * Saem da ligação boleto × veículo, e não do código do associado. Um dono com
 * três carros teria aqui os boletos dos três, e quem atende concluiria que este
 * carro deve o que outro deve. Quando a ligação não cobre o histórico, a tela
 * **diz** — lista curta sem aviso é a mesma mentira com outra cara.
 */
export function VehicleScreen() {
  const { code } = useParams({ from: '/authenticated/erp/veiculos/$code' });
  /* a página de cada tabela desta ficha, na URL — o "voltar" devolve a página */
  const pages = useSearch({ from: '/authenticated/erp/veiculos/$code' });
  const produtosPage = useTablePage('produtos', pages.produtos);
  const boletosPage = useTablePage('boletos', pages.boletos);
  const alteracoesPage = useTablePage('alteracoes', pages.alteracoes);
  const { data: context } = useSessionContext();

  /*
   * `erp.sync:executar` e não `erp.catalog:ver`: ler o espelho é leitura,
   * pedir atualização gasta o teto do fornecedor. É a mesma separação que o
   * botão da tela de saúde já faz.
   */
  const canRefresh = canAccess(context?.permissions ?? [], 'erp.sync', 'executar');

  /**
   * O "Atualizar do ERP" da ficha.
   *
   * Pede o VEÍCULO, e o pedido completo traz atrás dele o dono, os produtos e
   * o histórico inteiro de boletos, em qualquer situação (decisão de
   * 14/10/2026). A tela não pede boleto: se pedisse, teria que saber quais
   * existem — e é justamente o que ela não sabe quando o espelho está
   * incompleto.
   *
   * Pelo `erpCode`, que é o `codigo_veiculo` do SGA — nunca pelo `VE-000123`,
   * que é da casa. O código identifica UM veículo; a placa pode se repetir no
   * cadastro de lá. A placa fica para o veículo que a casa tem sem código do ERP.
   */
  const queryKey = ['erp', 'veiculo', code] as const;
  const { mutation: refresh, follow } = useErpRefresh(queryKey, () =>
    !v
      ? null
      : v.erpCode !== null
        ? { entity: 'veiculo', id: v.erpCode }
        : v.placa
          ? { entity: 'veiculo', id: v.placa, by: 'placa' }
          : null,
  );

  const query = useQuery({
    refetchInterval: follow,
    queryKey,
    queryFn: async () => {
      const r = await api.erp.vehicle({ params: { code } });
      if (r.status === 404)
        throw new NotFoundError('Este veículo não existe no cadastro desta base');
      if (r.status !== 200) {
        throw new Error(loadFailureMessage(r.status, 'o veículo'));
      }
      return r.body;
    },
  });

  const v = query.data;

  if (isNotFound(query.error)) {
    return (
      <MissingRecord
        title={query.error.message}
        list={{ to: '/erp/veiculos', label: 'Ver os veículos' }}
      />
    );
  }

  return (
    <Page measure="cheia">
      <Link
        to="/erp/veiculos"
        search={backSearch('/erp/veiculos') as never}
        className={backLinkClasses()}
      >
        Veículos
      </Link>

      <PageHeader title={v?.placa ?? (query.isLoading ? 'Carregando…' : 'Veículo')}>
        {v && (
          <>
            {[v.marca, v.modelo].filter(Boolean).join(' ')}
            {v.anoModelo && ` · ${v.anoModelo}`}
          </>
        )}
      </PageHeader>

      {query.isError && <Alert tone="erro">{query.error.message}</Alert>}

      {v && canRefresh && (v.placa || v.erpCode !== null) && (
        <div className="flex flex-wrap items-center gap-3">
          <Button
            onClick={() => refresh.mutate()}
            disabled={refresh.isPending}
            icon="atualizar"
          >
            {refresh.isPending ? 'Atualizando…' : 'Atualizar do ERP'}
          </Button>
          <span className="text-sm text-texto-suave">
            traz o veículo, o dono, os produtos e todo o histórico de boletos, em qualquer
            situação
          </span>
        </div>
      )}

      <RefreshOutcome mutation={refresh} />

      {v && (
        <div className="grid gap-4 lg:grid-cols-2">
          <Card title="O veículo" descricao={<MirrorStamp at={v.syncedAt} />}>
            <DataList>
              <DataRow label="Situação">
                <StatusBadge situacao={v.situacao} />
              </DataRow>
              <DataRow label="Placa">
                {v.placa ??
                  (v.invalidPlate === null ? (
                    <NotProvided />
                  ) : (
                    /* o ERP informou ALGO, e não é placa: dizer "não informado" seria falso */
                    <span>
                      <span className="text-texto-suave">no ERP consta </span>
                      {v.invalidPlate}
                      <span className="text-texto-suave">, que não é placa</span>
                    </span>
                  ))}
              </DataRow>
              <DataRow label="Chassi">{v.chassi ?? <NotProvided />}</DataRow>
              <DataRow label="Renavam">{v.renavam ?? <NotProvided />}</DataRow>
              <DataRow label="Categoria">{v.categoria ?? <NotProvided />}</DataRow>
              <DataRow label="Tipo">{v.tipo ?? <NotProvided />}</DataRow>
              <DataRow label="Ano de fabricação">
                {v.anoFabricacao ?? <NotProvided />}
              </DataRow>
              <DataRow label="Valor FIPE">
                <MoneyValue value={v.valorFipe} />
              </DataRow>
              <DataRow label="Contrato">
                {v.dataContrato ? formatDate(v.dataContrato) : <NotProvided />}
              </DataRow>
              {/*
               * Os dois códigos, e cada um responde uma pergunta diferente.
               * O da casa é o endereço desta ficha e nunca muda; o do ERP é
               * procedência — é o que se dita ao fornecedor, e o que o botão
               * acima manda. Regra 7.
               */}
              <DataRow label="Código">{v.code}</DataRow>
              <DataRow label="Código no ERP">
                {v.erpCode ?? <NotProvided>não veio do ERP</NotProvided>}
              </DataRow>
            </DataList>
          </Card>

          <Card
            title="O dono"
            descricao={
              v.owner && !v.owner.documentRevealed
                ? 'o documento aparece mascarado — revelar exige permissão própria'
                : undefined
            }
          >
            {v.owner === null ? (
              <p className="px-4 py-8 text-center text-sm text-texto-suave">
                Este veículo não aponta associado no cadastro do ERP.
              </p>
            ) : (
              <DataList>
                <DataRow label="Nome">{v.owner.nome ?? <NotProvided />}</DataRow>
                <DataRow label="Documento">
                  {v.owner.documento ? (
                    <span className="inline-flex items-center gap-2">
                      <span className="tabular-nums">{v.owner.documento}</span>
                      {!v.owner.documentRevealed && (
                        <Badge tone="neutro">mascarado</Badge>
                      )}
                    </span>
                  ) : (
                    <NotProvided />
                  )}
                </DataRow>
                <DataRow label="Tipo de pessoa">
                  {v.owner.tipoPessoa ?? <NotProvided />}
                </DataRow>
                <DataRow label="Situação">
                  {v.owner.situacao ? (
                    <StatusBadge situacao={v.owner.situacao} />
                  ) : (
                    <NotProvided />
                  )}
                </DataRow>
                <DataRow label="E-mail">{v.owner.email ?? <NotProvided />}</DataRow>
                <DataRow label="Telefones">
                  {v.owner.phones.length > 0 ? (
                    v.owner.phones.join(' · ')
                  ) : (
                    <NotProvided />
                  )}
                </DataRow>
                <DataRow label="Endereço">{v.owner.address ?? <NotProvided />}</DataRow>
                <DataRow label="Código no ERP">{v.owner.code}</DataRow>
                <DataRow label="Ficha">
                  <Link
                    to="/erp/associados/$code"
                    params={{ code: v.owner.code }}
                    className={linkTextClasses()}
                  >
                    Abrir a ficha do associado — atendimentos e alterações
                  </Link>
                </DataRow>
              </DataList>
            )}
          </Card>
        </div>
      )}

      {v && (
        <Card
          title={`Produtos deste veículo (${v.products.length})`}
          descricao={
            v.productsSyncedAt === null
              ? undefined
              : `consultados no ERP em ${formatInstant(v.productsSyncedAt)}`
          }
          bleed
        >
          {v.productsSyncedAt === null ? (
            /*
             * "Nunca perguntado" não é "não tem produto". A esteira consulta a
             * base aos poucos, e este carro ainda não teve a vez dele; o botão
             * acima consulta na hora. Lista vazia sem esta frase seria lida
             * como "sem cobertura".
             */
            <p className="px-4 py-8 text-center text-sm text-texto-suave">
              Os produtos deste veículo ainda não foram consultados no ERP.
              {canRefresh &&
                (v.placa || v.erpCode !== null) &&
                ' Use “Atualizar do ERP” para trazê-los.'}
            </p>
          ) : (
            <Table
              {...produtosPage}
              label="Produtos deste veículo"
              items={v.products}
              rowKey={(p) => p.erpCode}
              empty="O ERP não tem produto ligado a este veículo."
              columns={[
                {
                  title: 'Produto',
                  minWidth: '18rem',
                  cell: (p) => p.label ?? <NotProvided>código {p.erpCode}</NotProvided>,
                },
                {
                  title: 'Valor',
                  alignment: 'fim',
                  minWidth: '7rem',
                  /*
                   * Reais OU percentual, e o formato vem do SGA. Um percentual
                   * passado por `MoneyValue` sairia "R$ 10,00" onde o contrato
                   * diz 10%.
                   */
                  cell: (p) =>
                    p.percentage !== null ? (
                      <span className="tabular-nums">
                        {formatPercentage(p.percentage)}%
                      </span>
                    ) : (
                      <MoneyValue value={p.amount} />
                    ),
                },
                {
                  title: 'No boleto',
                  minWidth: '6rem',
                  cell: (p) =>
                    p.showOnBoleto === null ? (
                      <NotProvided>o ERP não disse</NotProvided>
                    ) : (
                      <Badge tone="neutro">
                        {p.showOnBoleto ? 'aparece' : 'não aparece'}
                      </Badge>
                    ),
                },
                {
                  title: 'Situação',
                  minWidth: '7rem',
                  cell: (p) =>
                    p.erpStatus === null ? (
                      <NotProvided />
                    ) : (
                      <Badge tone={p.erpStatus === 'ATIVO' ? 'sucesso' : 'neutro'}>
                        {p.erpStatus}
                      </Badge>
                    ),
                },
              ]}
            />
          )}
        </Card>
      )}

      {v && (
        <Card
          title={`Boletos deste veículo (${v.boletosTotal})`}
          descricao={
            v.boletosTotal > v.boletos.length
              ? `mostrando os ${v.boletos.length} mais recentes — a tela de boletos tem todos, com filtro`
              : undefined
          }
          bleed
        >
          <BoletosTable
            paging={boletosPage}
            label="Boletos deste veículo"
            items={v.boletos}
            empty="Nenhum boleto ligado a este veículo."
          />
        </Card>
      )}

      {v && (
        <ChangesCard changes={v.changes} of="neste veículo" paging={alteracoesPage} />
      )}
    </Page>
  );
}
